import { Button, Container, Stack, Box } from '@/ui';
import { Heading, Text } from '@/ui/typography';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform } from 'framer-motion';

import { ENGAGEMENT_EVENTS } from '@/shared/analytics';
import { useAnalytics } from '@/shared/hooks';

// Static animation variants - extracted for performance
const CONTAINER_VARIANTS = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.3,
            delayChildren: 0.2,
        }
    }
} as const;

const ITEM_VARIANTS = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 1.2,
            ease: [0.16, 1, 0.3, 1] as const,
        }
    }
} as const;

// Gradient styles moved to BackgroundOrbs component

export const HeroSection = (): React.JSX.Element => {
    const { t } = useTranslation();
    const { trackEngagement } = useAnalytics();
    const { scrollY } = useScroll();

    // Parallax effects for background elements
    const opacity = useTransform(scrollY, [0, 300], [1, 0]);

    const handleCTAClick = useCallback(() => {
        trackEngagement(ENGAGEMENT_EVENTS.CTA_CLICKED, {
            cta_name: 'hero_start_creating',
            cta_location: 'hero_section',
            cta_text: t('ui.hero.startCreating'),
            destination_url: '/workshop',
        });
    }, [trackEngagement, t]);

    return (
        <section style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* Clean, minimal background with parallax */}
            <motion.div
                style={{
                    opacity,
                    position: 'absolute',
                    inset: 0,
                    zIndex: -10
                }}
            >
                <Box style={{
                    position: 'absolute',
                    top: '25%',
                    left: '33%',
                    width: '500px',
                    height: '500px',
                    background: 'hsl(var(--primary-color) / 0.1)',
                    borderRadius: '9999px',
                    filter: 'blur(96px)'
                }} />
                <Box style={{
                    position: 'absolute',
                    bottom: '33%',
                    right: '33%',
                    width: '400px',
                    height: '400px',
                    background: 'hsl(var(--accent-color) / 0.1)',
                    borderRadius: '9999px',
                    filter: 'blur(96px)'
                }} />
            </motion.div>

            {/* Soft fade at bottom for section transition */}
            <Box style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 'var(--container-200)',
                background: 'linear-gradient(to top, hsl(var(--background)), hsl(var(--background) / 0.8), transparent)',
                pointerEvents: 'none'
            }} />

            <Container padding="lg" size="6xl" center style={{ position: 'relative', zIndex: 10 }}>
                <motion.div
                    variants={CONTAINER_VARIANTS}
                    initial="hidden"
                    animate="visible"
                >
                    <Stack align="center" gap={8}>
                        <motion.div variants={ITEM_VARIANTS}>
                            <Heading level={1} size="display-1" weight="normal" align="center">
                                {t('ui.hero.titleLine1')}
                                <br />
                                {t('ui.hero.titleLine2')}
                            </Heading>
                        </motion.div>

                        <motion.div variants={ITEM_VARIANTS}>
                            <Container padding="sm" size="3xl" center>
                                <Text align="center">
                                    {t('ui.hero.subtitle')}
                                    <br />
                                    {t('ui.hero.noSkillsNeeded')}
                                </Text>
                            </Container>
                        </motion.div>

                        <motion.div variants={ITEM_VARIANTS}>
                            <Stack align="center" gap={12}>
                                <Container size="sm">
                                    <Stack gap={3}>
                                        <Button
                                            asChild
                                            variant="premium"
                                            size="xl"
                                            fullWidth
                                            onClick={handleCTAClick}
                                        >
                                            <Link to="/workshop">
                                                {t('ui.hero.startCreating')}
                                            </Link>
                                        </Button>
                                        <Button
                                            asChild
                                            variant="ghost"
                                            size="lg"
                                            fullWidth
                                        >
                                            <Link to="/chat">
                                                {t('ui.hero.chatWithAI', 'Chat with AI ✨')}
                                            </Link>
                                        </Button>
                                    </Stack>
                                </Container>

                                {/* Scroll indicator - flows below button */}
                                <Stack align="center" gap={2} style={{ opacity: 0.6, animation: 'bounce 1s infinite' }}>
                                    <Text size="xs" color="muted" align="center">Scroll to explore</Text>
                                    <svg
                                        width="24"
                                        height="24"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        style={{ color: 'hsl(var(--muted-foreground))' }}
                                    >
                                        <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                                    </svg>
                                </Stack>
                            </Stack>
                        </motion.div>
                    </Stack>
                </motion.div>
            </Container>
        </section>
    );
};
